import React, { FC } from "react";
import { useTranslation } from "react-i18next";

import { DateSelector } from "./DateSelector";
import { NavigationToggleSwitch } from "./NavigationToggleSwitch";

import { FreeDrawArrow, FreeDrawText } from "@/sx-layout/components/plotmap/components/freeDraw/models";
import { ArrowTool } from "@/sx-layout/components/plotmap/components/toolbar/components/ArrowTool";
import { CustomKeepOutAreaTool } from "@/sx-layout/components/plotmap/components/toolbar/components/CustomKeepOutAreaTool";
import { TextTool } from "@/sx-layout/components/plotmap/components/toolbar/components/TextTool";
import { PlotPlan, PlotPlanType } from "@/sx-layout/components/settings/plotPlanSetting/models";

type Props = {
  displayAreaDetail: boolean;
  opacity: number;
  displayNavi: number;
  onChangeDisplayAreaDetail: (_: boolean) => void;
  onChangeOpacity: (_: number) => void;
  onChangeMapDisplayNavi: (_: number) => void;
  onZoomOut: () => void;
  onZoomIn: () => void;
  magnificationRatio: number;
  onDisplayHelpModal: () => void;
  plotPlan?: PlotPlan;
  // Freee draw (area)
  isCustomAreaDrawing: boolean;
  onCancelDrawingArea: () => void;
  // Free draw (arrow)
  isArrowDrawing: boolean;
  selectedArrow?: FreeDrawArrow;
  disableFreeDraw: boolean;
  arrowColor?: string;
  arrowWidth?: number;
  onClickEditArrow: () => void;
  onClickCancelArrow: () => void;
  onClickCopyArrow: () => void;
  onClickDeleteArrow: () => void;
  // Free draw (text)
  selectedText?: FreeDrawText;
  textColor: string;
  textSize: number;
  onClickEditText: () => void;
  onClickCopyText: () => void;
  onClickDeleteText: () => void;
};

export const NavigationToolbar: FC<Props> = ({
  displayAreaDetail,
  opacity,
  displayNavi,
  onChangeDisplayAreaDetail,
  onChangeOpacity,
  onChangeMapDisplayNavi,
  onZoomOut,
  onZoomIn,
  magnificationRatio,
  onDisplayHelpModal,
  plotPlan,
  disableFreeDraw,
  isCustomAreaDrawing,
  onCancelDrawingArea,
  isArrowDrawing,
  selectedArrow,
  arrowColor,
  arrowWidth,
  onClickEditArrow,
  onClickCancelArrow,
  onClickCopyArrow,
  onClickDeleteArrow,
  selectedText,
  textColor,
  textSize,
  onClickEditText,
  onClickCopyText,
  onClickDeleteText,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center justify-start space-x-6 h-[32px]">
        <DateSelector />
        {isCustomAreaDrawing && <CustomKeepOutAreaTool onCancel={onCancelDrawingArea} />}
        {(isArrowDrawing || !!selectedArrow) && (
          <ArrowTool
            isDrawing={isArrowDrawing}
            selectedArrow={selectedArrow}
            arrowColor={arrowColor}
            arrowWidth={arrowWidth}
            onClickEdit={onClickEditArrow}
            onClickCancel={onClickCancelArrow}
            onClickCopy={onClickCopyArrow}
            onClickDelete={onClickDeleteArrow}
            disabled={disableFreeDraw}
          />
        )}
        {!!selectedText && (
          <TextTool
            selectedText={selectedText}
            color={textColor}
            fontSize={textSize}
            onClickEdit={onClickEditText}
            onClickCopy={onClickCopyText}
            onClickDelete={onClickDeleteText}
            disabled={disableFreeDraw}
          />
        )}
      </div>
      <div className="flex items-center space-x-8">
        {plotPlan?.plot_plan_type === PlotPlanType.OVERVIEW && (
          <NavigationToggleSwitch
            label={t("area_detail")}
            leftOption={{
              label: t("display"),
              onClick: () => onChangeDisplayAreaDetail(true),
              isActive: !!displayAreaDetail,
            }}
            rightOption={{
              label: t("non_display"),
              onClick: () => onChangeDisplayAreaDetail(false),
              isActive: !displayAreaDetail,
            }}
          />
        )}
        <NavigationToggleSwitch
          label={t("floor_plan")}
          leftOption={{
            label: t("light"),
            onClick: () => onChangeOpacity(1),
            isActive: !!opacity,
          }}
          rightOption={{
            label: t("dark"),
            onClick: () => onChangeOpacity(0),
            isActive: !opacity,
          }}
        />
        <NavigationToggleSwitch
          label={t("navigation")}
          leftOption={{
            label: t("display"),
            onClick: () => onChangeMapDisplayNavi(1),
            isActive: !!displayNavi,
          }}
          rightOption={{
            label: t("non_display"),
            onClick: () => onChangeMapDisplayNavi(0),
            isActive: !displayNavi,
          }}
        />
        <div className="flex items-center space-x-4">
          <button type="button" onClick={onZoomOut} className="cursor-pointer">
            <img src="./img/icon-zoom-out.svg" className="w-[24px] h-[24px]" />
          </button>
          <span className="w-[40px] text-[13px] text-center whitespace-nowrap">{`${Math.round(
            magnificationRatio
          )}%`}</span>
          <button type="button" onClick={onZoomIn} className="cursor-pointer">
            <img src="./img/icon-zoom-in.svg" className="w-[24px] h-[24px]" />
          </button>
        </div>
        <div className="flex space-x-4 ">
          <button type="button" onClick={onDisplayHelpModal} className="cursor-pointer">
            <img src="./img/icon-help.svg" className="w-[18px] h-[18px]" />
          </button>
        </div>
      </div>
    </div>
  );
};
